import {FC, useState, useMemo, useCallback} from "react";
import {Box, Stack, LinearProgress, Typography, Link} from "@mui/material";
import {Delete, EventNote} from "@mui/icons-material";
import {Link as RouterLink} from "react-router-dom";
import {useGetUserActiveCourses, ActiveCourseDto} from "@/app2/shared/data";
import {BulkUnenrollModal} from "../components/BulkUnenrollModal";
import {ManageDueDatesModal} from "../../Courses/AdminCourseLandingPage/Tabs/BulkEnrollment/components/ManageDueDatesModal";
import {useManageUserCourseDueDates} from "../hooks/useManageUserCourseDueDates";
import { CustomToolbar } from "@/components/admin/bulkEnrollmentPlaylist/CustomToolbar";
import useDevCycleSdk from "@/app2/shared/hooks/useDevCycleSdk";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import { DataGridPro, GridColDef, GridSelectionModel } from "@mui/x-data-grid-pro";

interface UserActiveCoursesTabProps {
	userId: number;
	archived?: boolean;
}

/**
 * Admin view of a user's active courses
 *
 * Shows courses in progress with unenroll action.
 */
export const UserActiveCoursesTab: FC<UserActiveCoursesTabProps> = ({userId, archived}) => {
	const {data, isLoading, refetch} = useGetUserActiveCourses(userId);
	const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);
	const [bulkUnenrollOpen, setBulkUnenrollOpen] = useState(false);
	const [manageDueDatesOpen, setManageDueDatesOpen] = useState(false);
	const {archiveUserFlow, playlists: playlistsEnabled, dueDateAssignments} = useDevCycleSdk();
	const {t} = useSSTranslation();
	const {manageDueDatesForCourses, isPending: isManageDueDatesPending} = useManageUserCourseDueDates(userId);

	// Get selected course IDs for bulk unenroll
	const selectedCourseIds = useMemo(() => {
		return (data?.data ?? [])
			.filter((course) => selectedIds.includes(course.id))
			.map((course) => course.id);
	}, [data, selectedIds]);

	const columns: GridColDef<ActiveCourseDto>[] = [
		{
			field: "title",
			headerName: "Course",
			flex: 1,
			minWidth: 240
		},
		...(playlistsEnabled
			? [
					{
						field: "playlists",
						headerName: "Playlist",
						flex: 1,
						minWidth: 160,
						sortable: false,
						renderCell: (params: {row: ActiveCourseDto}) => {
							const pl = params.row.playlists;
							if (!pl?.length) {
								return <Typography variant="body2">—</Typography>;
							}
							return (
								<Stack spacing={0.25} sx={{py: 0.5}}>
									{pl.map((p) => (
										<Link
											key={p.id}
											component={RouterLink}
											to={`/admin/playlists/${p.id}/courses`}
											variant="body2"
											color="inherit"
											underline="hover"
										>
											{p.title?.trim() ? p.title : `Playlist ${p.id}`}
										</Link>
									))}
								</Stack>
							);
						}
					} as GridColDef<ActiveCourseDto>
				]
			: []),
		{
			field: "moduleCount",
			headerName: "Lessons",
			width: 100
		},
		{
			field: "dueOn",
			headerName: "Due On",
			flex: 1,
			valueFormatter: (params) => {
				if (!params.value) return "-";
				return new Date(params.value as string).toLocaleDateString();
			}
		},
		{
			field: "assignedOn",
			headerName: "Assigned On",
			flex: 1,
			valueFormatter: (params) => {
				if (!params.value) return "-";
				return new Date(params.value as string).toLocaleDateString();
			}
		},
		{
			field: "enrollmentType",
			flex: 1,
			headerName: t("common_enrollment_type", "Enrollment Type"),
			type: "string",
			valueGetter: (params) =>
				params.value
					? t(`user_enrollment_type_${params.value}` as "user_enrollment_type_learner", params.value as string)
					: ""
		},
		{
			field: "contentType",
			headerName: "Content Type",
			flex: 1,
			valueGetter: (params) => params.row.contentType ?? "-"
		},
		{
			field: "progress",
			headerName: "Progress",
			flex: 1,
			renderCell: (params) => {
				const progress = params.value as number;
				return (
					<Stack direction="row" alignItems="center" spacing={1} sx={{width: "100%"}}>
						<LinearProgress
							variant="determinate"
							value={progress}
							sx={{flexGrow: 1, height: 8, borderRadius: 4}}
						/>
						<Typography variant="caption" sx={{minWidth: 35}}>
							{progress}%
						</Typography>
					</Stack>
				);
			}
		}
	];

	const handleBulkUnenroll = () => {
		if (selectedCourseIds.length > 0) {
			setBulkUnenrollOpen(true);
		}
	};

	const handleBulkUnenrollSuccess = () => {
		setSelectedIds([]);
		setBulkUnenrollOpen(false);
		refetch();
	};

	const handleManageDueDates = useCallback(() => {
		if (selectedCourseIds.length > 0) {
			setManageDueDatesOpen(true);
		}
	}, [selectedCourseIds.length]);

	const handleManageDueDatesSuccess = () => {
		setSelectedIds([]);
		setManageDueDatesOpen(false);
		refetch();
	};

	return (
		<Box>
			{/* Data Grid */}
			<DataGridPro
				rows={data?.data ?? []}
				columns={columns}
				loading={isLoading}
				getRowId={(row) => row.id}
				checkboxSelection
				disableSelectionOnClick
				selectionModel={selectedIds}
				onSelectionModelChange={setSelectedIds}
				rowsPerPageOptions={[25, 50, 100, 200]}
				components={{
					Toolbar: (): JSX.Element => (
						<CustomToolbar>
							{(!archived || !archiveUserFlow) && (
								<Box onClick={handleBulkUnenroll}>
									<Delete /> {t("common_unenroll", "UNENROLL")}
								</Box>
							)}
							{dueDateAssignments && (
								<Box onClick={handleManageDueDates}>
									<EventNote /> {t("manage_due_dates_toolbar", "MANAGE DUE DATES")}
								</Box>
							)}
						</CustomToolbar>
					)
				}}
				initialState={{
					pagination: {pageSize: 25},
					sorting: {sortModel: [{field: "assignedOn", sort: "desc"}]}
				}}
				autoHeight
				sx={{
					"& .MuiDataGrid-row": {cursor: "default"}
				}}
			/>

			{/* Bulk Unenroll Modal */}
			<BulkUnenrollModal
				open={bulkUnenrollOpen}
				onClose={() => setBulkUnenrollOpen(false)}
				onSuccess={handleBulkUnenrollSuccess}
				userId={userId}
				courseIds={selectedCourseIds}
			/>

			<ManageDueDatesModal
				open={manageDueDatesOpen}
				onClose={() => setManageDueDatesOpen(false)}
				isPending={isManageDueDatesPending}
				onApply={({mode, dueDate}) => {
					void manageDueDatesForCourses(
						{courseIds: selectedCourseIds, mode, dueDate},
						{onSuccess: handleManageDueDatesSuccess}
					);
				}}
			/>
		</Box>
	);
};

